import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faEye,
  faEyeSlash,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../services/Interceptor";
import {
  getAthleteProfile,
  updateAthleteProfile,
} from "../services/AthleteService";
import { updateAthletePassword } from "../services/AdminService";
import { toast } from "react-toastify";
import { faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { positions, states } from "../utils/PositionStateData";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

export default function AthleteProfileModal({ athlete, onClose, onSave }) {
  const authUser = useAuthUser();
  const isAdmin = authUser?.user?.role === "admin";
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const apiInstance = useAxiosInstance();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordSubmitError, setPasswordSubmitError] = useState("");
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);

  const {
    register: passwordRegister,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    getValues: getPasswordValues,
    clearErrors: clearPasswordErrors,
    formState: { errors: passwordErrors, isValid: isPasswordFormValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const [profileData, setProfileData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    gpa: "",
    highSchool: "",
    expectedYearOfGraduation: "",
    city: "",
    state: "",
    primaryPosition: "",
    secondaryPosition: "",
    universityOfInterest: "",
    academicDegreeOfInterest: "",
    instagramHandle: "",
    twitterHandle: "",
    athleteEmail: "",
    parentEmail: "",
    athletePhone: "",
    parentPhone: "",
    transcriptFilePath: "",
    flxId: "",
    universitiesAttended: [],
  });

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await getAthleteProfile(apiInstance, athlete.userId);

        setProfileData({
          ...response,
          username: response.username || response.email || "",
          email: response.email || response.username || "",
          firstName: response.firstName ?? response.name ?? "",
          lastName: response.lastName ?? response.lastname ?? "",
          universitiesAttended: Array.isArray(response.universitiesAttended)
            ? response.universitiesAttended
            : [],
        });
      } catch (error) {}
    };

    if (athlete && athlete.userId) {
      getProfile();
    }
  }, [athlete, apiInstance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => {
      if (name === "username") {
        return {
          ...prev,
          username: value,
          email: value,
        };
      }

      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    //  Universities validation
    const universityError = validateUniversitiesAttended(
      profileData.universitiesAttended,
    );

    if (universityError) {
      toast.error(universityError);
      return;
    }

    try {
      const currentUsernameOrEmail = profileData.username || profileData.email;
      const updatedProfileData = isAdmin
        ? profileData
        : {
            ...profileData,
            username: currentUsernameOrEmail,
            email: currentUsernameOrEmail,
          };
      const updatedProfile = await updateAthleteProfile(
        apiInstance,
        athlete.userId,
        updatedProfileData,
      );
      setProfileData((prev) => ({
        ...prev,
        ...updatedProfile,
        username:
          updatedProfile?.username || updatedProfile?.email || prev.username,
        email: updatedProfile?.email || updatedProfile?.username || prev.email,
      }));
      onSave?.(updatedProfile);
      toast.success("Athlete profile updated successfully!");
      handleClose();
    } catch (error) {
      toast.error(
        "Error updating athlete profile: " +
          (error.response?.data?.message || "Unknown error"),
      );
    }
  };

  const handleUniversityChange = (index, field, value) => {
    const updated = [...profileData.universitiesAttended];
    updated[index][field] = value;

    setProfileData({
      ...profileData,
      universitiesAttended: updated,
    });
  };

  const addUniversity = () => {
    if (
      !profileData.universitiesAttended ||
      profileData.universitiesAttended.length === 0
    ) {
      setProfileData({
        ...profileData,
        universitiesAttended: [{ school: "", year: "", className: "" }],
      });
      return;
    }

    const last =
      profileData.universitiesAttended[
        profileData.universitiesAttended.length - 1
      ];

    if (
      !last.school ||
      String(last.year ?? "").trim() === "" ||
      !last.className
    ) {
      toast.error(
        "Please fill all fields of the current university before adding another.",
      );
      return;
    }

    setProfileData({
      ...profileData,
      universitiesAttended: [
        ...profileData.universitiesAttended,
        { school: "", year: "", className: "" },
      ],
    });
  };

  const removeUniversity = (index) => {
    const updated = profileData.universitiesAttended.filter(
      (_, i) => i !== index,
    );

    setProfileData({
      ...profileData,
      universitiesAttended: updated,
    });
  };

  const validateUniversitiesAttended = (universities) => {
    if (!Array.isArray(universities) || universities.length === 0) {
      return null;
    }

    for (let i = 0; i < universities.length; i++) {
      const { school, year, className } = universities[i];

      if (!school?.trim()) return `University name is required (Row ${i + 1}).`;
      if (!year || Number(year) <= 0)
        return `Valid year is required (Row ${i + 1}).`;
      if (!className?.trim()) return `Class name is required (Row ${i + 1}).`;
    }

    return null;
  };

  function shouldShowDeleteIcon(universities = []) {
    if (!Array.isArray(universities) || universities.length === 0) return false;
    if (universities.length > 1) return true;
    const u = universities[0] || {};
    const isEmpty =
      (!u.school || u.school.trim() === "") &&
      (!u.className || u.className.trim() === "") &&
      (!u.year || Number(u.year) === 0);

    return !isEmpty;
  }

  const showDeleteIcon = shouldShowDeleteIcon(profileData.universitiesAttended);

  const getApiErrorMessage = (error, fallbackMessage) => {
    if (typeof error === "string") return error;
    if (error?.message && typeof error.message === "string")
      return error.message;
    if (error?.error && typeof error.error === "string") return error.error;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.data?.error) return error.response.data.error;
    return fallbackMessage;
  };

  const openPasswordTab = () => {
    setActiveTab("password");
    setPasswordSubmitError("");
    clearPasswordErrors();
    resetPasswordForm();
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
  };

  const handlePasswordUpdate = async (data) => {
    setPasswordSubmitError("");

    try {
      setIsPasswordUpdating(true);
      await updateAthletePassword(
        apiInstance,
        athlete.userId,
        data.newPassword,
      );
      toast.success("Password updated successfully.");
      resetPasswordForm();
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        "Failed to update athlete password.",
      );
      setPasswordSubmitError(message);
    } finally {
      setIsPasswordUpdating(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 relative">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            {/* Header */}
            <h2 className="text-lg font-semibold mb-2 uppercase">
              {athlete?.name} {athlete?.lastname}
            </h2>

            {/* Tabs */}
            <div className="flex border-b mb-4">
              <button
                onClick={() => setActiveTab("profile")}
                className={`px-4 py-2 font-medium ${
                  activeTab === "profile"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Athlete Profile
              </button>

              <button
                onClick={openPasswordTab}
                className={`px-4 py-2 font-medium ${
                  activeTab === "password"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Password
              </button>

              <button
                onClick={() => setActiveTab("offers")}
                className={`px-4 py-2 font-medium hidden ${
                  activeTab === "offers"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Offers
              </button>
            </div>

            {/* Athlete Profile Content */}
            {activeTab === "profile" && (
              <form onSubmit={handleSubmit} className="w-full text-sm">
                <div className="flex flex-wrap -mx-3 overflow-y-auto overflow-x-hidden max-h-[500px]">
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="username"
                    >
                      Username/Email
                    </label>
                    <input
                      name="username"
                      className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 ${
                        !isAdmin ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      id="username"
                      type="email"
                      value={profileData.username || profileData.email || ""}
                      onChange={handleChange}
                      readOnly={!isAdmin}
                      disabled={!isAdmin}
                    />
                  </div>

                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="firstName"
                    >
                      First Name
                    </label>
                    <input
                      name="firstName"
                      className="appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900"
                      id="firstName"
                      type="text"
                      value={profileData.firstName ?? profileData.name ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="lastName"
                    >
                      Last Name
                    </label>
                    <input
                      name="lastName"
                      className="appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900"
                      id="lastName"
                      type="text"
                      value={profileData.lastName ?? profileData.lastname ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="flxId"
                    >
                      Flx Id
                    </label>
                    <div className="relative mb-2">
                      <input
                        name="flxId"
                        className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900}`}
                        id="flxId"
                        type="text"
                        value={profileData.flxId ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="primaryPosition"
                    >
                      Primary Position
                    </label>
                    <div className="relative mb-2">
                      <select
                        name="primaryPosition"
                        className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-2.5 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500}`}
                        id="primaryPosition"
                        value={profileData.primaryPosition || ""}
                        onChange={handleChange}
                      >
                        {positions.map((position) => (
                          <option key={position} value={position}>
                            {position}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="w-full md:w-2/4 px-3 mb-6 md:mb-0">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="secondaryPosition"
                    >
                      Secondary Position
                    </label>
                    <div className="relative">
                      <select
                        name="secondaryPosition"
                        className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-2.5 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500}`}
                        id="secondaryPosition"
                        value={profileData.secondaryPosition || ""}
                        onChange={handleChange}
                      >
                        {positions.map((position) => (
                          <option key={position} value={position}>
                            {position}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="w-full md:w-1/2 px-3 pt-0">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="highSchool"
                    >
                      High School
                    </label>
                    <input
                      name="highSchool"
                      className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900}`}
                      id="highSchool"
                      type="text"
                      value={profileData.highSchool ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full md:w-1/2 px-3 pt-0 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="highSchool"
                    >
                      Graduation year
                    </label>
                    <input
                      name="GraduationYear"
                      className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900}`}
                      id="GraduationYear"
                      type="text"
                      value={profileData.expectedYearOfGraduation ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full md:w-1/2 px-3 pt-0 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="city"
                    >
                      City
                    </label>
                    <input
                      name="city"
                      className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                      id="city"
                      type="text"
                      value={profileData.city ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full md:w-2/4 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="state"
                    >
                      State
                    </label>
                    <div className="relative">
                      <select
                        name="state"
                        className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-2.5 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500}`}
                        id="state"
                        value={profileData.state ?? ""}
                        onChange={handleChange}
                      >
                        {states.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/*  GPA */}
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="gpa"
                    >
                      GPA
                    </label>
                    <input
                      name="gpa"
                      className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                      id="gpa"
                      type="text"
                      value={profileData.gpa ?? ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-wrap px-3 -mx-3">
                    <div className="w-full md:w-1/2 px-3 md:mb-0">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="universityOfInterest"
                      >
                        University of Interest
                      </label>
                      <input
                        name="universityOfInterest"
                        className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                        id="universityOfInterest"
                        type="text"
                        value={profileData.universityOfInterest ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-2">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="academicDegreeOfInterest"
                      >
                        Academic Degree of Interest
                      </label>
                      <input
                        name="academicDegreeOfInterest"
                        className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                        id="academicDegreeOfInterest"
                        type="text"
                        value={profileData.academicDegreeOfInterest ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-2">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="instagram"
                      >
                        Instagram
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faInstagram}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        />
                        <input
                          name="instagram"
                          className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pl-10 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                          id="instagram"
                          type="text"
                          value={profileData.instagram ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="w-full md:w-1/2 px-3 mb-2">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="twitter"
                      >
                        Twitter
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faXTwitter}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        />
                        <input
                          name="twitter"
                          className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pl-10 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                          id="twitter"
                          type="text"
                          value={profileData.twitter ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="w-full md:w-1/2 px-3 mb-2">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="athleteEmail"
                      >
                        Athlete Email
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        />
                        <input
                          name="athleteEmail"
                          className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pl-10 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                          id="athleteEmail"
                          type="email"
                          value={profileData.athleteEmail ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-2">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="parentEmail"
                      >
                        Parent Email
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        />
                        <input
                          name="parentEmail"
                          className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pl-10 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900}`}
                          id="parentEmail"
                          type="email"
                          value={profileData.parentEmail ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="w-full md:w-1/2 px-3 mb-2">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="athletePhone"
                      >
                        Athlete Phone
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faPhone}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        />

                        <input
                          name="athletePhone"
                          className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pl-10 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                          id="athletePhone"
                          type="tel" // better than type="number" for phone numbers
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={profileData.athletePhone ?? ""}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(
                              /\D/g,
                              "",
                            ); // Remove non-digits
                            handleChange({
                              target: {
                                name: e.target.name,
                                value: numericValue,
                              },
                            });
                          }}
                        />
                      </div>
                    </div>

                    <div className="w-full md:w-1/2 px-3 mb-1">
                      <label
                        className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                        htmlFor="parentPhone"
                      >
                        Parent Phone
                      </label>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faPhone}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        />
                        <input
                          name="parentPhone"
                          className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pl-10 mb-2 leading-tight focus:outline-none focus:bg-white placeholder-gray-900 }`}
                          id="parentPhone"
                          type="tel" // better than type="number" for phone numbers
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={profileData.parentPhone ?? ""}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(
                              /\D/g,
                              "",
                            ); // Remove non-digits
                            handleChange({
                              target: {
                                name: e.target.name,
                                value: numericValue,
                              },
                            });
                          }}
                        />
                      </div>
                    </div>
                    {/* Universities Attended */}
                    <div className="w-full px-3 mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold uppercase text-gray-600">
                          Universities Attended
                        </h3>
                        <button
                          type="button"
                          onClick={addUniversity}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          + Add University
                        </button>
                      </div>

                      {Array.isArray(profileData.universitiesAttended) &&
                        profileData.universitiesAttended.length > 0 &&
                        profileData.universitiesAttended.map((uni, index) => (
                          <div
                            key={index}
                            className="flex flex-wrap items-center gap-2 mb-3"
                          >
                            <input
                              type="text"
                              placeholder="University Name"
                              value={uni.school}
                              onChange={(e) =>
                                handleUniversityChange(
                                  index,
                                  "school",
                                  e.target.value,
                                )
                              }
                              className="flex-1 border border-gray-300 rounded p-2 text-sm"
                            />

                            <input
                              type="text"
                              placeholder="Year"
                              value={uni.year}
                              onChange={(e) =>
                                handleUniversityChange(
                                  index,
                                  "year",
                                  e.target.value,
                                )
                              }
                              className="w-28 border border-gray-300 rounded p-2 text-sm"
                            />

                            <input
                              type="text"
                              placeholder="Class (ex: Freshman)"
                              value={uni.className}
                              onChange={(e) =>
                                handleUniversityChange(
                                  index,
                                  "className",
                                  e.target.value,
                                )
                              }
                              className="w-40 border border-gray-300 rounded p-2 text-sm"
                            />

                            {showDeleteIcon && (
                              <button
                                type="button"
                                onClick={() => removeUniversity(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-600 border rounded-md hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Update
                  </button>
                </div>
              </form>
            )}

            {activeTab === "password" && (
              <form
                onSubmit={handlePasswordSubmit(handlePasswordUpdate)}
                className="px-2 text-sm"
              >
                <div
                  className="flex items-start sm:items-center p-4 mb-4 text-sm text-fg-brand-strong rounded bg-blue-100"
                  role="alert"
                >
                  <svg
                    className="w-4 h-4 me-2 shrink-0 mt-0.5 sm:mt-0"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                  <p>
                    <span className="font-medium me-1"> Note:</span> If you
                    change this password, the athlete will not be able to log in
                    to the Trackitt Portal using their previous credentials.
                    Please ensure the new password is shared with the athlete.
                  </p>
                </div>
                <div className="flex flex-wrap -mx-6 px-2 overflow-y-auto h-[330px]">
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="newPassword"
                    >
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        autoComplete="new-password"
                        {...passwordRegister("newPassword", {
                          required: "New password is required.",
                          onChange: () => {
                            if (passwordSubmitError) setPasswordSubmitError("");
                          },
                        })}
                        className="appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pr-10 leading-tight focus:outline-none focus:bg-white"
                        placeholder="Enter new password..."
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        aria-label={
                          showNewPassword
                            ? "Hide new password"
                            : "Show new password"
                        }
                        aria-pressed={showNewPassword}
                      >
                        <FontAwesomeIcon
                          icon={showNewPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="text-red-500 text-xs mt-1">
                        {passwordErrors.newPassword.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full md:w-1/2 px-3 mb-2">
                    <label
                      className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                      htmlFor="confirmNewPassword"
                    >
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmNewPassword"
                        type={showConfirmNewPassword ? "text" : "password"}
                        autoComplete="new-password"
                        {...passwordRegister("confirmNewPassword", {
                          required: "Confirm new password is required.",
                          validate: (value) =>
                            value === getPasswordValues("newPassword") ||
                            "New Password and Confirm New Password must match.",
                          onChange: () => {
                            if (passwordSubmitError) setPasswordSubmitError("");
                          },
                        })}
                        className="appearance-none block w-full text-gray-900 border border-gray-300 rounded p-2.5 pr-10 leading-tight focus:outline-none focus:bg-white"
                        placeholder="Confirm new password..."
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmNewPassword((prev) => !prev)
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                        aria-label={
                          showConfirmNewPassword
                            ? "Hide confirm new password"
                            : "Show confirm new password"
                        }
                        aria-pressed={showConfirmNewPassword}
                      >
                        <FontAwesomeIcon
                          icon={showConfirmNewPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                    {passwordErrors.confirmNewPassword && (
                      <p className="text-red-500 text-xs mt-1">
                        {passwordErrors.confirmNewPassword.message}
                      </p>
                    )}
                  </div>
                </div>

                {passwordSubmitError && (
                  <p className="text-red-500 text-xs mb-3">
                    {passwordSubmitError}
                  </p>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="submit"
                    disabled={!isPasswordFormValid || isPasswordUpdating}
                    className={`px-4 py-2 text-white rounded-md ${
                      !isPasswordFormValid || isPasswordUpdating
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {isPasswordUpdating ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            )}

            {/* Offers Content */}
            {activeTab === "offers" && (
              <div className="p-4 text-gray-700 text-sm hidden">
                <p>
                  <strong>Current Offers:</strong>
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>University of Michigan – Full Scholarship Offer</li>
                  <li>Stanford University – 50% Athletic Scholarship</li>
                  <li>UCLA – Walk-On Opportunity</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
